import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryParam, NumberParam, withDefault } from 'use-query-params';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TableContainer,
  TablePagination
} from '@material-ui/core';

import { formatDate } from '../../../common/formatter';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import PageTemplateCardOver from '../../../common/page-template/PageTemplateCardOver';
import UserLink from '../../users/components/UserLink';
import { Scar } from '../../scar/Scar.interface';
import { useFindScarsBySupplier } from '../../scar/Scar.hook';
import ScarCreateDialog from '../../scar/ScarCreateDialog';
import { Supplier } from '../Supplier.interface';
import AppSnackbar from '../../../common/AppSnackbar';

const SupplierCorrectiveActionTabInfo: React.FC<{
  supplier: Supplier;
  handleAddItem?: (callback?: () => void) => void;
}> = ({ supplier, handleAddItem }) => {
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const history = useHistory();
  const [open, setOpen] = useState<boolean>(false);
  const [didCreate, setDidCreate] = useState<boolean>(false);
  const [{ data: scarList }, get] = useFindScarsBySupplier(supplier.id);

  const getWithParams = useCallback(() => {
    const params = {
      page: page,
      limit: limit
    };
    get(params);
  }, [page, limit, get]);

  useEffect(() => {
    getWithParams();
  }, [getWithParams]);

  useEffect(() => {
    handleAddItem?.(() => setOpen(true));
    return () => handleAddItem?.(undefined);
  }, [handleAddItem]);

  return (
    <PageTemplateCardOver>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Company Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Created Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scarList && scarList.total === 0 ? (
              <TableRow>
                <TableCell colSpan={5} style={{ borderBottom: 'unset' }}>
                  <Typography variant='body1'>No records found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              scarList &&
              scarList.data.map((scar: Scar) => (
                <TableRow
                  hover
                  key={scar.id}
                  onClick={() =>
                    history.push(`/portal/scar/task/${scar.taskId}`)
                  }
                >
                  <TableCell>{scar.supplier?.companyName}</TableCell>
                  <TableCell>{scar.description}</TableCell>
                  <TableCell>
                    <TaskStatusLabel task={scar.task} />
                  </TableCell>
                  <TableCell>
                    <UserLink
                      type='name'
                      user={scar.createdBy}
                      disableLink={true}
                    />
                  </TableCell>
                  <TableCell>{formatDate(scar.created)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component='div'
        count={scarList?.total || 0}
        rowsPerPage={scarList?.limit || 100}
        page={scarList?.page ? scarList?.page - 1 : 0}
        onPageChange={(_, page: number) => setPage(page)}
        onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setLimit(+event.target.value);
          setPage(0);
        }}
      />

      <ScarCreateDialog
        open={open}
        supplierId={supplier.id}
        disableSupplierSelect
        onClose={() => setOpen(false)}
        onCancel={() => setOpen(false)}
        onCreate={() => {
          setOpen(false);
          setDidCreate(true);
          getWithParams();
        }}
      />

      <AppSnackbar
        severity='success'
        open={didCreate}
        onClose={() => setDidCreate(false)}
      >
        SCAR created successfully.
      </AppSnackbar>
    </PageTemplateCardOver>
  );
};
export default SupplierCorrectiveActionTabInfo;
